import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { getEligibleProducts } from '../data/products';
import { Copy, Check, ExternalLink, Wrench, ShieldAlert, Package } from 'lucide-react';
import { toast } from 'sonner';

// Helpful Tools section — partner products. Renders nothing when no eligible
// product is found for the current submission. Used on the Result page.
export default function HelpfulToolsSection({ submission }) {
  const { lang, t } = useLanguage();
  const products = getEligibleProducts(submission);
  const [copiedKey, setCopiedKey] = useState(null);
  const [manualKey, setManualKey] = useState(null);

  if (!products || products.length === 0) return null;

  const copyCode = async (code) => {
    // Try the modern async clipboard API first.
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
        ok = true;
      }
    } catch { /* fall through to legacy fallback */ }

    // Fallback: hidden textarea + execCommand('copy'). Works inside many
    // sandboxed iframes where the async Clipboard API is blocked.
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, code.length);
        ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }

    if (ok) {
      setCopiedKey(code);
      setManualKey(null);
      toast.success(t('helpful_code_copied'));
      setTimeout(() => setCopiedKey(null), 1800);
    } else {
      // Last-resort: visibly mark the code for manual long-press copy.
      setManualKey(code);
      setCopiedKey(null);
      toast.error(t('helpful_copy_manual'));
      // Keep the highlight on screen long enough for the user to act.
      setTimeout(() => setManualKey((k) => (k === code ? null : k)), 8000);
    }
  };

  return (
    <section className="mb-6" data-testid="helpful-tools-section">
      <h2 className="font-heading text-lg font-semibold text-ink mb-3 flex items-center gap-2">
        <Wrench size={18} className="text-terracotta" />
        {t('helpful_tools_title')}
      </h2>
      <p className="text-xs text-ink-tertiary leading-relaxed mb-4" data-testid="helpful-tools-disclosure">
        {t('helpful_tools_disclosure')}
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {products.map((p) => {
          const name = lang === 'zh' ? p.name_zh : p.name_en;
          const tagline = lang === 'zh' ? p.tagline_zh : p.tagline_en;
          const safety = lang === 'zh' ? p.safety_note_zh : p.safety_note_en;
          const ctaLabel = lang === 'zh' ? p.cta_label_zh : p.cta_label_en;
          const extra = p.extra_note ? p.extra_note(submission, lang) : null;

          return (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="rounded-3xl bg-paper border border-border overflow-hidden flex flex-col"
            >
              {p.image_url ? (
                <div
                  className="h-32 sm:h-36 bg-cover bg-center bg-subtle"
                  style={{ backgroundImage: `url(${p.image_url})` }}
                  aria-hidden="true"
                />
              ) : (
                <div
                  className="h-24 sm:h-28 bg-gradient-to-br from-terracotta-light via-paper to-sage-light/40 flex items-center justify-center"
                  aria-hidden="true"
                >
                  <Package size={28} className="text-terracotta/60" />
                </div>
              )}
              <div className="p-5 flex flex-col gap-3 flex-1">
                <div>
                  <h3 className="font-heading text-base font-semibold text-ink leading-snug">{name}</h3>
                  {tagline && (
                    <p className="text-xs text-ink-tertiary mt-1 leading-relaxed">{tagline}</p>
                  )}
                </div>

                {/* Discount codes */}
                {p.discounts && p.discounts.length > 0 && (
                  <div className="space-y-2" data-testid={`product-discounts-${p.id}`}>
                    {p.discounts.map((d) => {
                      const desc = lang === 'zh' ? d.desc_zh : d.desc_en;
                      const isCopied = copiedKey === d.code;
                      const isManual = manualKey === d.code;
                      return (
                        <div
                          key={d.code}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border ${isManual ? 'bg-amber-50 border-amber-300' : 'bg-terracotta-light/40 border-terracotta/20'}`}
                        >
                          <div className="min-w-0 flex-1">
                            {/* user-select-all + tabIndex make long-press / triple-click copy work */}
                            <div
                              className="font-mono text-sm font-semibold text-terracotta select-all cursor-text"
                              data-testid={`code-${p.id}-${d.code}`}
                              tabIndex={0}
                            >
                              {d.code}
                            </div>
                            {desc && <div className="text-[11px] text-ink-tertiary leading-tight mt-0.5">{desc}</div>}
                            {isManual && (
                              <div className="text-[11px] text-amber-700 leading-tight mt-1" data-testid={`manual-hint-${p.id}-${d.code}`}>
                                {t('helpful_copy_manual_hint')}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => copyCode(d.code)}
                            data-testid={`copy-${p.id}-${d.code}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-paper border border-terracotta/40 text-xs font-medium text-terracotta hover:bg-terracotta hover:text-white transition-colors flex-shrink-0"
                          >
                            {isCopied ? <Check size={12} /> : <Copy size={12} />}
                            {isCopied ? t('helpful_copied') : t('helpful_copy')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Extra contextual note (e.g. balm + latch) */}
                {extra && (
                  <p className="text-[11px] text-ink-secondary leading-relaxed italic" data-testid={`extra-note-${p.id}`}>
                    {extra}
                  </p>
                )}

                {/* Safety note */}
                {safety && (
                  <div className="flex items-start gap-2 text-[11px] text-ink-tertiary leading-relaxed">
                    <ShieldAlert size={12} className="text-ink-tertiary flex-shrink-0 mt-0.5" />
                    <span>{safety}</span>
                  </div>
                )}

                {/* External link */}
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`product-link-${p.id}`}
                  className="mt-auto inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full bg-paper border-2 border-terracotta/30 text-sm font-medium text-terracotta hover:bg-terracotta hover:text-white hover:border-terracotta transition-colors"
                >
                  {ctaLabel}
                  <ExternalLink size={12} />
                </a>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
